/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { FocusableElement } from '#utils/PRIVATE_tabbableUtils';

/**
 * Returns the cell type for the given element
 */
export const getCellType = (element: FocusableElement) => {
  return element.dataset['ojCellType'];
};

/**
 * Returns the row key for the given element
 */
export const getRowKey = <K>(element: HTMLElement) => {
  return (
    element.dataset['ojKeyType'] === 'number'
      ? Number(element.dataset['ojKey'])
      : element.dataset['ojKey']
  ) as K;
};

/**
 * Returns the index of the given row element
 */
export const getRowIndexForRowElement = (
  rootElement: HTMLElement,
  rootId: string,
  rowElement: HTMLElement,
  rowSelector: string
) => {
  const rowElements = rootElement.querySelectorAll(`[${rowSelector}='${rootId}']`);
  const index = Array.prototype.indexOf.call(rowElements, rowElement);
  return index > -1 ? index : undefined;
};

/**
 * Returns the key of the first rendered row in the component
 */
export const getFirstRenderedRowKey = <K>(
  rootElement: HTMLElement,
  rootId: string,
  rowSelector: string
) => {
  const firstRowElement = rootElement.querySelector(`[${rowSelector}='${rootId}']`);
  return firstRowElement != null ? getRowKey<K>(firstRowElement as HTMLElement) : undefined;
};

/**
 * Returns the key of the last rendered row in the component
 */
export const getLastRenderedRowKey = <K>(
  rootElement: HTMLElement,
  rootId: string,
  rowSelector: string
) => {
  const rowElements = rootElement.querySelectorAll(`[${rowSelector}='${rootId}']`);
  const rowCount = rowElements.length;
  return rowCount > 0 ? getRowKey<K>(rowElements[rowCount - 1] as HTMLElement) : undefined;
};

/**
 * Helper method to get the 'no data' element
 */
export const getElementBySelector = (
  rootElement: HTMLElement,
  rootId: string,
  elementSelector: string
) => {
  const element = rootElement.querySelector(`[${elementSelector}='${rootId}']`);
  return element != null ? (element as HTMLElement) : undefined;
};
